import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Plus, Edit2, Trash2, Loader2, X, CheckCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import ConditionBuilder, { Condition } from '../components/ConditionBuilder';

interface AutoTagRule {
  id: number;
  key: string;
  label: string;
  description?: string;
  explanation?: string;
  color: string;
  targetType: 'customer' | 'referrer';
  conditions: Condition[];
  priority: number;
  isActive: number | boolean;
  whatsappTemplateKey?: string;
  exclusiveGroup?: string | null;
  createdAt?: number;
  updatedAt?: number;
}

type TargetType = 'customer' | 'referrer';

const COLORS = [
  { value: 'gray', label: '灰色' },
  { value: 'blue', label: '藍色' },
  { value: 'green', label: '綠色' },
  { value: 'yellow', label: '黃色' },
  { value: 'red', label: '紅色' },
  { value: 'purple', label: '紫色' },
  { value: 'orange', label: '橙色' },
];

function toBool(v: number | boolean | undefined): boolean {
  return v === 1 || v === true;
}

function colorBadgeClass(color: string): string {
  const map: Record<string, string> = {
    gray: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600',
    blue: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
    green: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
    yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800',
    red: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
    purple: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
    orange: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800',
  };
  return map[color] || map.gray;
}

function operatorLabel(op: string): string {
  const map: Record<string, string> = {
    eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', contains: '包含', in: '在列表',
  };
  return map[op] || op;
}

function fieldLabel(_targetType: TargetType, field: string): string {
  const map: Record<string, string> = {
    orderCount: '訂單數量',
    totalSpent: '累計消費',
    daysSinceLastOrder: '距離上次下單天數',
    hasReferrer: '是否有推薦人',
    source: '來源',
    name: '姓名',
    phone: '電話',
    directRefs: '直接推薦數',
    indirectRefs: '間接推薦數',
    networkSize: '網絡人數',
    totalOrderValue: '累計訂單額',
    totalCommission: '累計佣金',
    tier: '等級',
  };
  return map[field] || field;
}

function summarizeConditions(conditions: Condition[], targetType: TargetType): string {
  if (!Array.isArray(conditions) || conditions.length === 0) return '無條件（永遠符合）';
  return conditions
    .map((c) => `${fieldLabel(targetType, c.field)} ${operatorLabel(c.operator)} ${String(c.value)}`)
    .join('，');
}

const defaultForm = {
  key: '',
  label: '',
  description: '',
  explanation: '',
  color: 'gray',
  targetType: 'customer' as TargetType,
  priority: 0,
  isActive: true,
  whatsappTemplateKey: '',
  exclusiveGroup: '',
  conditions: [] as Condition[],
};

const AdminAutoTags: React.FC = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const token = localStorage.getItem('admin_token');

  const [targetType, setTargetType] = useState<TargetType>('customer');
  const [rules, setRules] = useState<AutoTagRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    loadRules();
  }, [token, navigate, targetType]);

  const loadRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/auto-tag-rules?target_type=${targetType}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '獲取規則失敗');
      setRules(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e.message || '獲取規則失敗');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...defaultForm, targetType });
    setShowModal(true);
  };

  const openEdit = (rule: AutoTagRule) => {
    setEditingId(rule.id);
    setForm({
      key: rule.key || '',
      label: rule.label || '',
      description: rule.description || '',
      explanation: rule.explanation || '',
      color: rule.color || 'gray',
      targetType: rule.targetType || targetType,
      priority: rule.priority ?? 0,
      isActive: toBool(rule.isActive),
      whatsappTemplateKey: rule.whatsappTemplateKey || '',
      exclusiveGroup: (rule as any).exclusiveGroup || '',
      conditions: Array.isArray(rule.conditions) ? rule.conditions : [],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(defaultForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE)) {
      setError('沒有權限');
      return;
    }
    if (!form.key.trim() || !form.label.trim()) {
      setError('請填寫規則代碼與名稱');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    const payload = {
      key: form.key.trim(),
      label: form.label.trim(),
      description: form.description.trim() || undefined,
      explanation: form.explanation.trim() || undefined,
      color: form.color,
      targetType: form.targetType,
      priority: Number(form.priority) || 0,
      isActive: form.isActive,
      whatsappTemplateKey: form.whatsappTemplateKey.trim() || undefined,
      exclusiveGroup: form.exclusiveGroup.trim() || undefined,
      conditions: form.conditions.map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
      })),
    };

    try {
      const url = editingId
        ? `/api/public/admin/auto-tag-rules/${editingId}`
        : '/api/public/admin/auto-tag-rules';
      const method = editingId ? 'PUT' : 'POST';
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '儲存失敗');
      setSuccess(editingId ? '規則已更新' : '規則已建立');
      closeModal();
      loadRules();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這條自動標籤規則嗎？')) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/public/admin/auto-tag-rules/${id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '刪除失敗');
      setSuccess('規則已刪除');
      loadRules();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  return (
    <AdminLayout currentPage="auto-tags" onLogout={handleLogout}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Tag className="w-6 h-6 text-orange-600" />
          自動標籤規則
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">根據客戶或推薦人屬性自動加上標籤</p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span className="flex items-center gap-1"><CheckCircle className="w-4 h-4" /> {success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-1 inline-flex">
        {(['customer', 'referrer'] as TargetType[]).map((t) => (
          <button
            key={t}
            onClick={() => setTargetType(t)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              targetType === t
                ? 'bg-orange-600 text-white'
                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t === 'customer' ? '客戶標籤規則' : '推薦人標籤規則'}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="mb-4 flex justify-end">
        {can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE) && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> 新增規則
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">標籤</th>
                <th className="p-4">代碼</th>
                <th className="p-4">優先級</th>
                <th className="p-4">互斥組</th>
                <th className="p-4">狀態</th>
                <th className="p-4">WhatsApp 模板</th>
                <th className="p-4">條件摘要</th>
                <th className="p-4 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-400">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> 加載中...
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500 dark:text-gray-400">
                    暫無{targetType === 'customer' ? '客戶' : '推薦人'}標籤規則
                  </td>
                </tr>
              ) : (
                rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-orange-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorBadgeClass(rule.color)}`}>
                        {rule.label}
                      </span>
                    </td>
                    <td className="p-4 font-mono text-gray-600 dark:text-gray-300">{rule.key}</td>
                    <td className="p-4 text-gray-800 dark:text-white">{rule.priority}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{(rule as any).exclusiveGroup || '-'}</td>
                    <td className="p-4">
                      {toBool(rule.isActive) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300">啟用</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">停用</span>
                      )}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{rule.whatsappTemplateKey || '-'}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300 max-w-sm truncate" title={summarizeConditions(rule.conditions, rule.targetType)}>
                      {summarizeConditions(rule.conditions, rule.targetType)}
                    </td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      {can(PERMISSIONS.CUSTOMER_JOURNEYS_MANAGE) && (
                        <>
                          <button
                            onClick={() => openEdit(rule)}
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                          >
                            <Edit2 size={14} /> 編輯
                          </button>
                          <button
                            onClick={() => handleDelete(rule.id)}
                            className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-xs"
                          >
                            <Trash2 size={14} /> 刪除
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingId ? '編輯自動標籤規則' : '新增自動標籤規則'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">規則代碼 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={form.key}
                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value }))}
                    placeholder="例如：high-value-customer"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標籤名稱 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={form.label}
                    onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    placeholder="例如：大額客戶"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顏色</label>
                  <select
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  >
                    {COLORS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優先級</label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">互斥組（可選）</label>
                <input
                  type="text"
                  value={form.exclusiveGroup}
                  onChange={(e) => setForm((f) => ({ ...f, exclusiveGroup: e.target.value }))}
                  placeholder="例如：order_count_tier，同組只保留最高優先級標籤"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">同一互斥組內，系統只會保留符合條件且優先級最高的標籤。</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">目標類型</label>
                <input
                  type="text"
                  disabled
                  value={form.targetType === 'customer' ? '客戶' : '推薦人'}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="簡短描述規則用途"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">說明文字（可選）</label>
                <input
                  type="text"
                  value={form.explanation}
                  onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))}
                  placeholder="顯示給管理員的備註"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">WhatsApp 模板代碼（可選）</label>
                <input
                  type="text"
                  value={form.whatsappTemplateKey}
                  onChange={(e) => setForm((f) => ({ ...f, whatsappTemplateKey: e.target.value }))}
                  placeholder="例如：order_received_v1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 text-orange-600 rounded"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">啟用規則</label>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">條件設定</label>
                <ConditionBuilder
                  conditions={form.conditions}
                  onChange={(conditions) => setForm((f) => ({ ...f, conditions }))}
                  targetType={form.targetType}
                />
              </div>

              <div className="flex gap-2 pt-2 sticky bottom-0 bg-white dark:bg-gray-800 pb-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? '儲存變更' : '建立規則'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminAutoTags;
